import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';

const PaymentConfirmationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');

    if (!paymentIntent || !paymentIntentClientSecret) {
      setError('Invalid payment confirmation');
      setLoading(false);
      return;
    }

    // Verify payment status with backend
    const verifyPayment = async () => {
      try {
        const response = await fetch(`/api/payments/verify/${paymentIntent}`);
        if (!response.ok) {
          throw new Error('Failed to verify payment');
        }

        const data = await response.json();
        setPaymentStatus(data.status === 'succeeded' ? 'success' : 'error');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setPaymentStatus('error');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {paymentStatus === 'success' ? (
          <>
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Payment Successful
            </Typography>
            <Typography color="text.secondary" paragraph>
              Thank you for your payment. Your order has been confirmed.
            </Typography>
          </>
        ) : (
          <>
            <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Payment Failed
            </Typography>
            <Typography color="text.secondary" paragraph>
              We were unable to process your payment. Please try again.
            </Typography>
          </>
        )}

        <Box mt={3}>
          <Button
            variant="contained"
            onClick={() => navigate('/orders')}
            sx={{ mr: 2 }}
          >
            View Orders
          </Button>
          {paymentStatus === 'error' && (
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Try Again
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentConfirmationPage; 